import React from "react"
import { pickLocalizedCopy } from "@/lib/locale-copy"

// The boundary is a class component (it must be, to catch render errors), so it
// cannot use the i18n hook. Reading the locale store imperatively at render is
// enough here and keeps the fallback from being hardcoded Catalan (BUG-091).
const ERROR_BOUNDARY_COPY = {
	ca: { title: "Hi ha hagut un error", unexpected: "Error inesperat", reload: "Recarregar pàgina" },
	es: { title: "Se ha producido un error", unexpected: "Error inesperado", reload: "Recargar página" },
	en: { title: "Something went wrong", unexpected: "Unexpected error", reload: "Reload page" },
}

interface Props {
	children: React.ReactNode
	fallback?: React.ReactNode
	/** When this value changes the boundary clears its error (e.g. the route path). */
	resetKey?: string
}

interface State {
	hasError: boolean
	error: Error | null
}

export class ErrorBoundary extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props)
		this.state = { hasError: false, error: null }
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error }
	}

	componentDidUpdate(prevProps: Props) {
		// Without this a single failed page keeps every other route on the error
		// screen until a full reload, because the layout (and this boundary) stays
		// mounted across client-side navigation.
		if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
			this.setState({ hasError: false, error: null })
		}
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error("[ErrorBoundary] Caught error:", error, errorInfo)
	}

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback
			}
			const copy = pickLocalizedCopy(ERROR_BOUNDARY_COPY)
			return (
				<div className="flex flex-col items-center justify-center min-h-[40vh] p-8">
					<div className="text-center max-w-md">
						<div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
							<svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
							</svg>
						</div>
						<h2 className="text-lg font-semibold text-gray-900 mb-2">
							{copy.title}
						</h2>
						<p className="text-sm text-gray-500 mb-4">
							{this.state.error?.message || copy.unexpected}
						</p>
						<button
							onClick={() => {
								this.setState({ hasError: false, error: null })
								window.location.reload()
							}}
							className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-800 transition-colors"
						>
							{copy.reload}
						</button>
					</div>
				</div>
			)
		}

		return this.props.children
	}
}
